import { Box } from '@mui/material'
import React, { ReactElement } from 'react'
import { HIGH_NOMINAL_POWER_CALCULATION_LIMIT } from 'src/Libs/flow/installation/utils'

interface FormulaContentProps {
  nominalPower: number
}

/** Component for the limit formula content. */
export const FormulaContent: React.FC<FormulaContentProps> = ({ nominalPower }) => {
  return <Box>{nominalPower <= HIGH_NOMINAL_POWER_CALCULATION_LIMIT ? <LowPowerFormulaContent /> : <HighPowerFormulaContent />}</Box>
}

const LowPowerFormulaContent = (): ReactElement => {
  return (
    <Box>
      <FormulaWrapper>
        <FormulaText>
          P<sub>min,&nbsp;14a</sub> = 4,2&nbsp;kW&nbsp;+&nbsp;(n<sub>steuVE</sub>
          &nbsp;&#8209;&nbsp;1)&nbsp;x&nbsp;GZF&nbsp;x&nbsp;4,2&nbsp;kW
        </FormulaText>
        <Box height={16} />
        <FormulaText>
          P<sub>min,&nbsp;14a</sub>: Mindestleistung
        </FormulaText>
        <FormulaText>
          n<sub>steuVE</sub>: Anzahl aller steuerbarer Verbrauchseinrichtungen
        </FormulaText>
        <FormulaText>GZF: anzuwendender Gleichzeitigkeitsfaktor</FormulaText>
      </FormulaWrapper>
      <GZFTable />
    </Box>
  )
}

const HighPowerFormulaContent = (): ReactElement => {
  return (
    <Box>
      <FormulaWrapper>
        <FormulaText>
          P<sub>min,&nbsp;14a</sub> = Max(0,4&nbsp;x&nbsp;P<sub>Summe&nbsp;WP</sub>; 0,4&nbsp;x&nbsp;P<sub>Summe&nbsp;Klima</sub>&nbsp;) + (n
          <sub>steuVE</sub>
          &nbsp;&#8209;&nbsp;1) x GZF x 4,2&nbsp;kW
        </FormulaText>
        <Box height={16} />
        <FormulaText>
          P<sub>min, 14a</sub>: Mindestleistung
        </FormulaText>
        <FormulaText>
          P<sub>Summe_WP</sub>: Summe der Netzanschlussleistungen der Anlagen
        </FormulaText>
        <FormulaText>
          P<sub>Summe Klima</sub> = Summe der Netzanschlussleistungen der Anlagen
        </FormulaText>
        <FormulaText>
          n<sub>steuVE</sub>: Anzahl aller steuerbarer Verbrauchseinrichtungen
        </FormulaText>
        <FormulaText>GZF: anzuwendender Gleichzeitigkeitsfaktor</FormulaText>
      </FormulaWrapper>
      <GZFTable />
    </Box>
  )
}

const GZFTable = (): ReactElement => {
  return (
    <Box
      component={'table'}
      sx={{
        border: '1px solid #9f9f9f',
        borderCollapse: 'collapse',
        'th, td': {
          border: '1px solid #9f9f9f',
          borderCollapse: 'collapse',
          fontSize: 14,
          fontWeight: 'normal',
          px: 0.5,
          textAlign: 'left',
        },
      }}
    >
      <thead>
        <tr>
          <th>
            n<sub>steuVE</sub>
          </th>
          <th>2</th>
          <th>3</th>
          <th>4</th>
          <th>5</th>
          <th>6</th>
          <th>7</th>
          <th>8</th>
          <th>{'>=9'}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>GZF</td>
          <td>0,8</td>
          <td>0,75</td>
          <td>0,7</td>
          <td>0,65</td>
          <td>0,6</td>
          <td>0,55</td>
          <td>0,5</td>
          <td>0,45</td>
        </tr>
      </tbody>
    </Box>
  )
}

const FormulaWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ fontSize: 14, lineHeight: 1.8, fontWeight: 'bold' }}>{children}</Box>
)

/** Paragraph element with the lines after the first one are indended. */
const FormulaText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component={'p'} sx={{ m: 0, pl: 3, textIndent: theme => theme.spacing(-3) }}>
    {children}
  </Box>
)
